import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Save } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DECLARATION_FIELDS = [
  {
    key: 'nccp_default_debts',
    label: 'Have you or a company with which you are associated had any past or current debts that were or are in default of the payment arrangement?',
  },
  {
    key: 'nccp_bankruptcy_insolvency',
    label: 'Have you been: bankrupt or insolvent, petitioned or aware of anyone petitioning you for bankruptcy, advised to seek bankruptcy, or associated with a company that was or threatened to be placed into administration?',
  },
  {
    key: 'nccp_adverse_credit_impact',
    label: 'Are you aware of anything that may adversely affect your application for credit or ability to meet your obligations under this loan (e.g. increase in expenses, change in employment, changes to family circumstances, etc.)?',
  },
  {
    key: 'nccp_future_household_change',
    label: 'Do you expect any significant adverse change to your household financial position in the future?',
  },
];

export default function ComplianceTab({ deal, dealId, onRefresh }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      nccp_default_debts: deal?.nccp_default_debts,
      nccp_bankruptcy_insolvency: deal?.nccp_bankruptcy_insolvency,
      nccp_adverse_credit_impact: deal?.nccp_adverse_credit_impact,
      nccp_future_household_change: deal?.nccp_future_household_change,
      nccp_additional_notes: deal?.nccp_additional_notes || '',
    });
  }, [deal]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/crm/deals/${dealId}`, form);
      toast.success('NCCP declarations saved');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save NCCP declarations');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="nccp-tab">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-white font-medium flex items-center gap-2" data-testid="nccp-tab-title">
          <ShieldCheck className="h-5 w-5 text-emerald-400" /> NCCP Consumer Compliance
        </h2>
        <Button
          onClick={handleSave}
          size="sm"
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="save-nccp-button"
        >
          <Save className="h-4 w-4 mr-1" />
          {saving ? 'Saving...' : 'Save Declarations'}
        </Button>
      </div>

      <div className="space-y-3">
        {DECLARATION_FIELDS.map((field) => (
          <div
            key={field.key}
            className="bg-navy-800/50 border border-white/10 rounded-xl p-4"
            data-testid={`nccp-question-${field.key}`}
          >
            <label className="block text-sm text-gray-200 mb-2">{field.label}</label>
            <select
              value={form[field.key] === true ? 'true' : form[field.key] === false ? 'false' : ''}
              onChange={(e) => {
                const raw = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  [field.key]: raw === '' ? null : raw === 'true',
                }));
              }}
              className="w-full md:w-72 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none"
              data-testid={`nccp-select-${field.key}`}
            >
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        ))}
      </div>

      <div className="bg-navy-800/50 border border-white/10 rounded-xl p-4" data-testid="nccp-notes-panel">
        <label className="block text-sm text-gray-200 mb-2" htmlFor="nccp-notes-input">
          Is There Anything You Want to Tell Us about your application?
        </label>
        <textarea
          id="nccp-notes-input"
          rows={4}
          value={form.nccp_additional_notes || ''}
          onChange={(e) => setForm((prev) => ({ ...prev, nccp_additional_notes: e.target.value }))}
          className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="nccp-additional-notes-input"
        />
      </div>
    </div>
  );
}